import React, { useState } from 'react';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ success: false, message: '' });
  const [errors, setErrors] = useState({});

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    
    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    // Phone validation - basic format check
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[0-9\s()-]{8,}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [id]: value
    }));
    
    // Clear error when user starts typing
    if (errors[id]) {
      setErrors(prev => ({
        ...prev,
        [id]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Send form data to Formspree
      const response = await fetch("https://formspree.io/f/xvoelkzb", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message,
          _subject: `New Contact Form Submission from ${formData.name}`,
          _replyto: formData.email,
          _cc: "info@mkavanco.co.tz" // Make sure this is in the Formspree approved senders
        }),
      });
      
      const responseData = await response.json();
      
      if (response.ok) {
        setSubmitStatus({
          success: true,
          message: 'Thank you for your message! We will get back to you soon.'
        });
        // Reset form after successful submission
        setFormData({ name: '', phone: '', email: '', message: '' });
      } else {
        throw new Error(responseData.error || 'Form submission failed');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setSubmitStatus({
        success: false,
        message: 'Failed to send message. Please try again later or contact us directly at info@mkavanco.co.tz'
      });
    }
    
    setIsSubmitting(false);
  };

  return (
    <section className="contact_section py-5">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-8 col-md-10 text-center">
            <div className="heading_container mb-4">
              <h2>Contact Us</h2>
              <p className="subheading">We'd love to hear from you! Reach out to us for any inquiries or feedback.</p>
            </div>
          </div>
        </div>
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="row align-items-stretch">
              <div className="col-lg-6 d-flex mb-4 mb-lg-0">
                <div className="form_container contact-form shadow-lg p-4 rounded flex-grow-1">
                  {submitStatus.message && (
                    <div className={`alert ${submitStatus.success ? 'alert-success' : 'alert-danger'} mb-4`}>
                      {submitStatus.message}
                    </div>
                  )}
                  <form onSubmit={handleSubmit}>
                    <div className="form-group mb-3">
                      <label htmlFor="name" className="form-label">Your Name</label>
                      <input 
                        type="text" 
                        id="name" 
                        name="name"
                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                        placeholder="Enter your name" 
                        value={formData.name}
                        onChange={handleChange}
                        required 
                      />
                      {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                    </div>
                    <div className="form-group mb-3">
                      <label htmlFor="phone" className="form-label">Phone Number</label>
                      <input 
                        type="tel" 
                        id="phone" 
                        name="phone"
                        className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                        placeholder="Enter your phone number (e.g., +255717039523)" 
                        value={formData.phone}
                        onChange={handleChange}
                        required 
                      />
                      {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                    </div>
                    <div className="form-group mb-3">
                      <label htmlFor="email" className="form-label">Email</label>
                      <input 
                        type="email" 
                        id="email" 
                        name="email"
                        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                        placeholder="Enter your email" 
                        value={formData.email}
                        onChange={handleChange}
                        required 
                      />
                      {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                    </div>
                    <div className="form-group mb-3">
                      <label htmlFor="message" className="form-label">Message</label>
                      <textarea 
                        id="message" 
                        name="message"
                        className={`form-control ${errors.message ? 'is-invalid' : ''}`}
                        placeholder="Type your message here..." 
                        rows="4" 
                        value={formData.message}
                        onChange={handleChange}
                        required
                      ></textarea>
                      {errors.message && <div className="invalid-feedback">{errors.message}</div>}
                    </div>
                    <div className="text-center">
                      <button 
                        type="submit" 
                        className="btn btn-danger px-4" 
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            SENDING...
                          </>
                        ) : 'SEND MESSAGE'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
              <div className="col-lg-6 d-flex">
                <div className="contact-info bg-light shadow-lg p-4 rounded flex-grow-1">
                  <h3 className="mb-4">Get in Touch</h3>
                  <div className="info-item mb-3">
                    <i className="bi bi-geo-alt me-2"></i>
                    <p>Mkavanco Company, Tanzania</p>
                  </div>
                  <div className="info-item mb-3">
                    <i className="bi bi-envelope me-2"></i>
                    <p>Email: <a href="mailto:info@mkavanco.co.tz">info@mkavanco.co.tz</a></p>
                  </div>
                  <div className="info-item mb-4">
                    <i className="bi bi-telephone me-2"></i>
                    <p>Call Us: <a href="tel:+255717039523">+255717039523</a></p>
                  </div>
                  <div id="googleMap" style={{height: "250px"}}>
                    {/* Google Map will be loaded here by the script */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Contact;